import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { GradientContainer } from "@/components/gradient-container/GradientContainer.tsx";
import { getTimeId } from "@/helpers/dates.helper.ts";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import i18n from "@/i18n.ts";
import { useBlogMyChannels, useBlogPostSave } from "@/queries/blog.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import getTimezones, { DefaultTimezone } from "@/services/timezone-service.ts";
import {
  ApiBlogChannelCan,
  ApiBlogPostScheduleSettings,
  BlogEditSteps,
  useBlogEditContext,
} from "@/types/blog.types.ts";
import { Time } from "@/types/common.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Checkbox,
  FullScreenPage,
  Select,
  Skeleton,
  Stack,
  Switch,
  Text,
  Title,
} from "@skailer-com/uikit";
import { formatISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

const buildBlogPostEditSettingsSchema = () =>
  yup.object({
    channel_ids: yup
      .array()
      .of(yup.number().required())
      .min(1, i18n.t("blog:error_need_one_feed_for_publishing"))
      .required(i18n.t("errors:field_required")),
    with_schedule: yup.boolean(),
    schedule_date: yup.date(),
    schedule_time: yup.date(),
    timezone: yup.string(),
    notification_app: yup.boolean(),
    notification_email: yup.boolean(),
    notification_slack: yup.boolean(),
    disable_reactions: yup.boolean(),
    disable_comments: yup.boolean(),
  });

export function BlogPostEditSettings() {
  const { setStep, post, clearAttachmentTracking } = useBlogEditContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: channels, isLoading } = useBlogMyChannels({ can: ApiBlogChannelCan.publish });
  const { mutateAsync: savePost, isPending } = useBlogPostSave();
  const { renderTime } = useFormatter();
  const today = useMemo(() => {
    const now = new Date();

    now.setMinutes(0);
    now.setHours(now.getHours() + 1);

    return now;
  }, []);
  const withScheduledLogic = useMemo(() => {
    if (!post?.datetime_published_at) return false;

    return post.datetime_published_at.getTime() > new Date().getTime();
  }, []);

  const { data: employeeMe } = useEmployeeMe();
  const blogPostEditSettingsSchema = buildBlogPostEditSettingsSchema();

  const {
    control,
    getValues,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(blogPostEditSettingsSchema),
    values: {
      channel_ids: post?.channels.map((channel) => channel.id) ?? [],
      notification_app: post?.notification_settings.in_app,
      notification_email: post?.notification_settings.email,
      notification_slack: post?.notification_settings.slack,
      with_schedule: withScheduledLogic,
      schedule_date: withScheduledLogic ? (post?.datetime_published_at ?? today) : today,
      schedule_time: withScheduledLogic ? (post?.datetime_published_at ?? today) : today,
      timezone: employeeMe?.timezone,
      disable_reactions: post?.reactions_disabled,
      disable_comments: post?.comments_disabled,
    },
  });

  const withSchedule = watch("with_schedule");

  const selectChannel = (channelId: number) => {
    setValue(
      "channel_ids",
      getValues("channel_ids").includes(channelId)
        ? getValues("channel_ids").filter((id) => id != channelId)
        : [...getValues("channel_ids"), channelId]
    );
  };

  const startTimesFull = useMemo(() => {
    const startTimes: Time[] = [];

    for (let i = 7; i <= 20; i++) {
      for (let j = 0; j < 60; j += 15) {
        if (i === 20 && j > 0) continue;
        const date = new Date(2000, 1, 1, i, j);

        startTimes.push({
          id: getTimeId(date),
          title: renderTime(date),
          date: date,
        });
      }
    }

    return startTimes;
  }, []);

  const onSubmit = handleSubmit(async (data) => {
    const scheduleDate: Date | null = data.schedule_date ?? null;
    const timeZone: string = data.timezone ?? DefaultTimezone;
    let dateICOString: string | null = null;

    if (data.schedule_time) {
      scheduleDate?.setHours(data.schedule_time.getHours());
      scheduleDate?.setMinutes(data.schedule_time.getMinutes());
      scheduleDate?.setSeconds(0);
    }

    if (scheduleDate && data.schedule_time) {
      const dt = formatInTimeZone(new Date(), timeZone, "xxx");
      dateICOString = formatISO(scheduleDate).replace(/([+-]\d{2}:\d{2})$/, dt);
    }

    const schedule: ApiBlogPostScheduleSettings | null =
      data.with_schedule && dateICOString
        ? {
            datetime_publish_at: dateICOString,
            timezone: timeZone,
          }
        : null;

    const postSaved = await savePost({
      id: post?.id,
      payload: {
        title: post?.title ?? "",
        caption: post?.caption ?? "",
        body: post?.body ?? "",
        file_uuids: post?.files.map((file) => file.uuid) ?? [],
        channels: data.channel_ids ?? [],
        disable_comments: data.disable_comments ?? false,
        disable_reactions: data.disable_reactions ?? false,
        color: post?.color ?? null,
        hero_image_file_uuid: post?.hero_image_file?.file_uuid ?? null,
        notification_settings: {
          in_app: data.notification_app ?? false,
          email: data.notification_email ?? false,
          slack: data.notification_slack ?? false,
        },
        schedule_settings: schedule,
      },
    });

    analyticsService.trackEvent(
      post?.id ? analyticEvents.blog.postUpdated : analyticEvents.blog.postCreated,
      {
        [analyticProperties.id]: postSaved.id,
      }
    );

    if (postSaved) {
      clearAttachmentTracking();
      if (postSaved.channels.length > 0) {
        navigate(`/news/${postSaved.channels[0].id}/${postSaved.id}`);
      } else {
        navigate(`/news/posts/${postSaved.id}`);
      }
    }
  });

  return (
    <FullScreenPage
      title={t("blog:preview_post")}
      backSlot={
        <Button
          variant={"secondary"}
          size={"lg"}
          onClick={() => {
            navigate(-1);
          }}
        >
          {t("common:exit")}
        </Button>
      }
      backgroundSlot={
        post?.hero_image_file?.color_palette && (
          <GradientContainer
            colorPalette={post?.hero_image_file?.color_palette}
            addLightOverlayTop={true}
          />
        )
      }
      actionsSlot={
        <Stack direction={"horizontal"} gap={"sm"}>
          <Button
            onClick={() => {
              setStep(BlogEditSteps.preview);
            }}
            variant="secondary"
            size={"lg"}
          >
            {t("common:back")}
          </Button>
          <Button size={"lg"} onClick={onSubmit} isLoading={isPending}>
            {t("blog:publish")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit} className={"relative"}>
        <Stack className={"container-sm ml-auto mr-auto"} gap={"sm"}>
          <Stack className={"card-panel"} gap={"2xl"}>
            <Title header={t("blog:choose_channels")} />

            {isLoading && (
              <Stack gap={"md"}>
                <Skeleton className={"h-5 w-40"} />
                <Skeleton className={"h-5 w-40"} />
                <Skeleton className={"h-5 w-40"} />
              </Stack>
            )}

            <Stack gap={"sm"}>
              <Controller
                control={control}
                name={"channel_ids"}
                render={({ field: { value } }) => (
                  <>
                    {channels &&
                      channels.map((channel) => (
                        <Checkbox
                          key={`configure-channel-${channel.id}`}
                          checked={value.includes(channel.id)}
                          onChange={() => {
                            selectChannel(channel.id);
                          }}
                          emoji={channel.emoji}
                          title={channel.title}
                        />
                      ))}
                    {errors.channel_ids && (
                      <Box className={"pt-2 pb-4"}>
                        <Text className={"text-danger"}>{errors.channel_ids.message}</Text>
                      </Box>
                    )}
                  </>
                )}
              />
            </Stack>
          </Stack>
          <Stack className={"card-panel"} gap={"lg"}>
            <Title header={t("common:settings")} />
            <Stack gap={"sm"}>
              <Title
                className={"text-dark/60"}
                header={t("blog:notify_subscribers_in")}
                size={"xs"}
              />
              <Controller
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    checked={value}
                    onChange={onChange}
                    title={t("common:application")}
                    caption={t("blog:app_notification_caption")}
                  />
                )}
                name={"notification_app"}
              />
              <Controller
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Checkbox checked={value} onChange={onChange} title={t("core:email")} />
                )}
                name={"notification_email"}
              />
              <Controller
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    checked={value}
                    onChange={onChange}
                    title={t("integrations:slack_title")}
                  />
                )}
                name={"notification_slack"}
              />
            </Stack>
            <Stack gap={"sm"}>
              <Stack gap={"md"}>
                <Controller
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Switch title={t("blog:scheduled_post")} onChange={onChange} checked={value} />
                  )}
                  name={"with_schedule"}
                />
                {withSchedule && (
                  <Stack className={"pb-4"}>
                    <Stack gap={"sm"} direction={"horizontal"}>
                      <Box className={"w-full grow"}>
                        <Controller
                          control={control}
                          name={"schedule_date"}
                          render={({ field: { value, onChange } }) => (
                            <Datepicker
                              value={value}
                              onChange={onChange}
                              label={t("common:date")}
                            />
                          )}
                        />
                      </Box>
                      <Controller
                        control={control}
                        name={"schedule_time"}
                        render={({ field: { value, onChange } }) => (
                          <Select
                            value={value ? getTimeId(value) : null}
                            emptyTitle={t("common:time")}
                            onChange={(newValue) => {
                              onChange(startTimesFull.find((item) => item.id == newValue)?.date);
                            }}
                            options={startTimesFull}
                            searchable
                            error={errors.schedule_time?.message}
                          />
                        )}
                      />
                    </Stack>
                    <Stack gap={"sm"}>
                      <Controller
                        control={control}
                        name="timezone"
                        render={({ field: { value, onChange } }) => (
                          <Select
                            label={t("common:timezone")}
                            options={getTimezones()}
                            emptyTitle={t("common:select")}
                            searchable
                            value={value}
                            onChange={onChange}
                            error={errors.timezone?.message}
                          />
                        )}
                      />
                    </Stack>
                  </Stack>
                )}
              </Stack>
              <Controller
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Switch
                    title={t("blog:reactions_disabled_switch")}
                    onChange={onChange}
                    checked={value}
                  />
                )}
                name={"disable_reactions"}
              />
              <Controller
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Switch
                    title={t("blog:comments_disabled_switch")}
                    onChange={onChange}
                    checked={value}
                  />
                )}
                name={"disable_comments"}
              />
            </Stack>
          </Stack>
          <Stack className="pb-(--gap-3xl)">
            <ServerErrorField errors={errors} />
            <Button size={"lg"} onClick={onSubmit} isLoading={isPending}>
              {!withSchedule ? t("blog:publish") : t("blog:schedule")}
            </Button>
          </Stack>
        </Stack>
      </form>
    </FullScreenPage>
  );
}
